import React, { useRef } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { GridApi, GridReadyEvent, StateUpdatedEvent } from 'ag-grid-community';
import { usePersistentGrid } from '../../hooks/usePersistentGrid';
import GridHeaderTooltip from './GridHeaderTooltip';

type PersistentGridProps<TData> = AgGridReactProps<TData>;

// Grid footer showing "X of Y rows" (visible/filtered vs total) - same status panel
// DocLibraryComponent.tsx already uses. Set here so it's the default for every page built on
// PersistentGrid (the app's standard ag-grid wrapper), not something each page has to opt into
// individually. Spread AFTER this in the JSX below, so a page passing its own `statusBar` still
// overrides it, same precedence pattern used everywhere else in this app (e.g. buildDefaultColDef).
const DEFAULT_STATUS_BAR: AgGridReactProps['statusBar'] = {
    statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
    ],
};

// AG Grid's own default is 2000ms, which reads as sluggish for a simple header tooltip - fast
// enough to feel immediate without popping up on every incidental mouse-pass.
const TOOLTIP_SHOW_DELAY_MS = 200;

const PersistentGrid = <TData,>({ defaultColDef, onGridReady, ...agGridProps }: PersistentGridProps<TData>): React.ReactElement => {
    const { gridState, updateGridState, resetKey, setGridApi } = usePersistentGrid();
    const apiRef = useRef<GridApi | null>(null);

    function stateUpdated(event: StateUpdatedEvent) {
        const state = event.state;
        updateGridState(state);
    }

    function handleGridReady(event: GridReadyEvent) {
        apiRef.current = event.api;
        setGridApi(event.api); // shares the live api with sibling components, e.g. PersistentGridClearFiltersButton
        onGridReady?.(event);
    }

    // The floating-filter row has no built-in right-click gesture of its own - suppressing its
    // button icon below removes the only way ag-grid normally offers to open the full filter
    // editor from that row, so reimplement the same action as a right-click on the search box
    // instead, at the grid-container level (works for every column with no per-page code).
    // aria-colindex is the floating-filter cell's 1-based visual position, which lines up with
    // getAllDisplayedColumns()'s order - there's no col-id attribute on this particular cell to
    // read directly.
    function handleContextMenu(event: React.MouseEvent<HTMLDivElement>) {
        const cell = (event.target as HTMLElement).closest?.('.ag-floating-filter');
        if (!cell || !apiRef.current) return;
        const colIndexAttr = cell.getAttribute('aria-colindex');
        if (!colIndexAttr) return;
        const column = apiRef.current.getAllDisplayedColumns()[Number(colIndexAttr) - 1];
        if (!column) return;
        event.preventDefault();
        apiRef.current.showColumnFilter(column.getColId());
    }

    // Hide the always-visible filter-menu icons (the floating-filter row's own button, and the
    // separate small filter icon the header row itself can show) - same full filter editor is
    // still reachable, just via right-clicking the search box (handleContextMenu) instead of a
    // persistent icon. `buttons: ['clear']` adds a Clear button inside that editor. All merged
    // (not replaced) so a page's own defaultColDef/filterParams still win on any key it sets
    // explicitly, same precedence pattern used everywhere else in this app (e.g. buildDefaultColDef).
    const mergedDefaultColDef = {
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        filterParams: { buttons: ['clear'] },
        tooltipComponent: GridHeaderTooltip,
        ...defaultColDef,
    };

    return (
        <div onContextMenu={handleContextMenu} style={{ width: '100%', height: '100%' }}>
            <AgGridReact<TData>
                key={resetKey}  // Changing this key will trigger a full re-render
                statusBar={DEFAULT_STATUS_BAR}
                tooltipShowDelay={TOOLTIP_SHOW_DELAY_MS}
                {...agGridProps}
                defaultColDef={mergedDefaultColDef}
                onGridReady={handleGridReady}
                onStateUpdated={stateUpdated}
                initialState={gridState}
            />
        </div>
    );
};

export default PersistentGrid;
